import type { PromptInputMessage } from '@crowdex/ui/ai/prompt-input';
import { useCallback } from 'react';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';

export type FollowUpQueue = {
  removeQueuedFollowUp: (conversationId: string, id: string) => void;
  enqueueFollowUp: (conversationId: string, message: PromptInputMessage, id?: string) => void;
  reorderQueuedFollowUp: (conversationId: string, draggedId: string, targetId: string) => void;
  clearQueuedFollowUps: (conversationId: string) => void;
};

/**
 * Pure per-conversation follow-up queue operations backed by the store. The
 * Every operation receives its conversation explicitly. This is important for
 * async steering and send failures, which may settle after the user navigates
 * to another conversation.
 */
export function useFollowUpQueue(store: DesktopShellStore): FollowUpQueue {
  const removeQueuedFollowUp = useCallback(
    (conversationId: string, id: string) => {
      const { setQueuedFollowUps } = store.getState();
      setQueuedFollowUps((current) => ({
        ...current,
        [conversationId]: (current[conversationId] ?? []).filter((item) => item.id !== id),
      }));
    },
    [store],
  );

  const enqueueFollowUp = useCallback(
    (conversationId: string, message: PromptInputMessage, id: string = crypto.randomUUID()) => {
      const { setQueuedFollowUps } = store.getState();
      setQueuedFollowUps((current) => ({
        ...current,
        [conversationId]: [
          ...(current[conversationId] ?? []).filter((item) => item.id !== id),
          { id, message },
        ],
      }));
    },
    [store],
  );

  const reorderQueuedFollowUp = useCallback(
    (conversationId: string, draggedId: string, targetId: string) => {
      if (draggedId === targetId) return;
      const { setQueuedFollowUps } = store.getState();
      setQueuedFollowUps((current) => {
        const items = [...(current[conversationId] ?? [])];
        const from = items.findIndex((item) => item.id === draggedId);
        const to = items.findIndex((item) => item.id === targetId);
        if (from < 0 || to < 0) return current;
        const [moved] = items.splice(from, 1);
        if (moved === undefined) return current;
        items.splice(to, 0, moved);
        return { ...current, [conversationId]: items };
      });
    },
    [store],
  );

  const clearQueuedFollowUps = useCallback(
    (conversationId: string) => {
      const { setQueuedFollowUps, setPausedFollowUpQueues } = store.getState();
      setQueuedFollowUps((current) => ({ ...current, [conversationId]: [] }));
      setPausedFollowUpQueues((current) => ({ ...current, [conversationId]: undefined }));
    },
    [store],
  );

  return {
    removeQueuedFollowUp,
    enqueueFollowUp,
    reorderQueuedFollowUp,
    clearQueuedFollowUps,
  };
}
